/**
 * Lightweight NFT image cache utility
 * Caches contract-generated image URLs to avoid repeated contract calls
 */

interface CacheEntry {
  url: string;
  timestamp: number;
  chainId: number;
  contractAddress: string;
  tokenId: string;
}

interface CacheData {
  [key: string]: CacheEntry;
}

class NftImageCache {
  private cache: CacheData = {};
  private readonly CACHE_KEY = 'blessnet_nft_image_cache';
  private readonly CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
  private readonly MAX_ENTRIES = 500; // Limit cache size

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Generate cache key from contract address, token ID, and chain ID
   */
  private generateKey(contractAddress: string, tokenId: string, chainId: number): string {
    return `${chainId}_${contractAddress.toLowerCase()}_${tokenId}`;
  }

  /**
   * Get cached image URL if available and not expired
   */
  get(contractAddress: string, tokenId: string, chainId: number): string | null {
    const key = this.generateKey(contractAddress, tokenId, chainId);
    const entry = this.cache[key];

    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() - entry.timestamp > this.CACHE_EXPIRY) {
      delete this.cache[key];
      this.saveToStorage();
      return null;
    }

    return entry.url;
  }

  /**
   * Store image URL in cache
   */
  set(contractAddress: string, tokenId: string, chainId: number, url: string): void {
    const key = this.generateKey(contractAddress, tokenId, chainId);

    this.cache[key] = {
      url,
      timestamp: Date.now(),
      chainId,
      contractAddress: contractAddress.toLowerCase(),
      tokenId
    };

    // Cleanup if cache is getting too large
    this.cleanup();
    this.saveToStorage();
  }

  /**
   * Check if we have a cached entry (even if expired, for loading states)
   */
  has(contractAddress: string, tokenId: string, chainId: number): boolean {
    const key = this.generateKey(contractAddress, tokenId, chainId);
    return key in this.cache;
  }

  /**
   * Remove old entries if cache is too large
   */
  private cleanup(): void {
    const entries = Object.entries(this.cache);

    if (entries.length <= this.MAX_ENTRIES) {
      return;
    }

    // Sort by timestamp (oldest first) and remove oldest entries
    entries.sort(([, a], [, b]) => a.timestamp - b.timestamp);

    const toRemove = entries.length - this.MAX_ENTRIES;
    for (let i = 0; i < toRemove; i++) {
      delete this.cache[entries[i][0]];
    }
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    Object.keys(this.cache).forEach(key => {
      if (now - this.cache[key].timestamp > this.CACHE_EXPIRY) {
        delete this.cache[key];
      }
    });
    this.saveToStorage();
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache = {};
    this.saveToStorage();
  }

  /**
   * Load cache from localStorage
   */
  private loadFromStorage(): void {
    try {
      if (typeof window === 'undefined') return; // SSR safety

      const stored = localStorage.getItem(this.CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.cache = parsed;
        this.clearExpired(); // Clean up expired entries on load
      }
    } catch (error) {
      console.warn('Failed to load NFT image cache from storage:', error);
      this.cache = {};
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveToStorage(): void {
    try {
      if (typeof window === 'undefined') return; // SSR safety

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(this.cache));
    } catch (error) {
      console.warn('Failed to save NFT image cache to storage:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; oldestEntry: number | null; newestEntry: number | null } {
    const entries = Object.values(this.cache);
    const timestamps = entries.map(e => e.timestamp);

    return {
      size: entries.length,
      oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : null,
      newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : null
    };
  }
}

// Export singleton instance
export const nftImageCache = new NftImageCache();

// Export for debugging
export { NftImageCache };
